/**
 * A registry of "somewhere on this page there is work the user has not
 * saved yet".
 *
 * Forms each keep their own dirty flag, and nothing knew about all of
 * them at once. The mandatory-update screen needs that: it replaces the
 * whole app, and doing so while a clinician is halfway through a note
 * would throw the note away. So the block waits until nothing is
 * unsaved — the user can still save, and the next check (tab focus, the
 * nightly one, or the next sign-in) applies it.
 *
 * Registration happens inside `useUnsavedChangesSimple`, which every
 * guarded form already calls, so no form has to opt in. A form that
 * does not use that hook is invisible here — this is a safety net, not
 * a guarantee.
 *
 * Module-level rather than Redux on purpose: CacheBuster sits outside
 * the Redux provider in the tree, and this is ephemeral UI state that
 * nothing should persist or replay.
 */
const dirtySources = new Set();
const listeners = new Set();

const notify = () => {
  listeners.forEach(listener => {
    try {
      listener(dirtySources.size > 0);
    } catch {
      // A broken listener must not stop the others, and must never
      // propagate into the form that happened to trigger the change.
    }
  });
};

/**
 * Record whether one source (a form, a panel) holds unsaved work.
 *
 * @param {string|symbol} id stable per mounted source
 * @param {boolean} isDirty
 */
export const setUnsavedWork = (id, isDirty) => {
  if (id === undefined || id === null) return;
  const had = dirtySources.has(id);
  if (isDirty) {
    if (had) return;
    dirtySources.add(id);
  } else {
    if (!had) return;
    dirtySources.delete(id);
  }
  notify();
};

/** Forget a source entirely — call on unmount. */
export const clearUnsavedWork = id => setUnsavedWork(id, false);

/** Does anything on the page hold unsaved work right now? */
export const hasUnsavedWork = () => dirtySources.size > 0;

/**
 * What a form's state means to the two things that ask.
 *
 * A form in the middle of saving is asked two different questions, and
 * they have opposite answers. Should the user be stopped from leaving?
 * No — the save's own success handler is about to navigate, and a
 * "discard changes?" dialog in its path would break the save. Is there
 * work that would be lost if the app were replaced right now? Yes — the
 * request is in flight, and unmounting the form drops it.
 *
 * Every guarded form used to compute `dirty && !saving` and hand that
 * one value to both askers. For the registry that reads as "nothing
 * unsaved" the instant Save is pressed, so the mandatory-update screen
 * replaced the app mid-request and the edit was lost. The browser had
 * sent the preflight and nothing after it.
 *
 * @param {{ dirty?: boolean, saving?: boolean }} state
 * @returns {{ blocksNavigation: boolean, holdsWork: boolean }}
 */
export const getFormWorkState = ({ dirty = false, saving = false } = {}) => ({
  blocksNavigation: Boolean(dirty) && !saving,
  holdsWork: Boolean(dirty) || Boolean(saving),
});

/**
 * Watch the answer. Returns an unsubscribe function.
 *
 * @param {(hasUnsaved: boolean) => void} listener
 */
export const subscribeUnsavedWork = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
